import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ClientPlatform } from '../types/clientUpdate';

const LOCAL_WEB_BUILD_ENDPOINT = '/app-version.json';
const REMOTE_WEB_BUILD_PATH = '/app-version.json';
const CURRENT_WEB_BUILD_STORAGE_KEY = 'oraCurrentWebBuild';
const AUTO_RELOAD_GUARD_STORAGE_KEY = 'oraVersionAutoReloadGuard';
const AUTO_RELOAD_MAX_ATTEMPTS = 2;
const AUTO_RELOAD_WINDOW_MS = 5 * 60 * 1000;

type WebBuildIdentitySource = 'file' | 'storage' | 'none';

type ReloadGuardRecord = {
  count: number;
  windowStartedAt: number;
};

let cachedLocalWebBuildPromise: Promise<string | null> | null = null;
let autoReloadPending = false;

let cachedNativeAppInfoPromise: Promise<{
  appVersion?: string;
  appBuildNumber?: string;
}> | null = null;

let currentSessionWebBuildSource: WebBuildIdentitySource = 'none';

const isBrowser = (): boolean => {
  return typeof window !== 'undefined';
};

const normalizeOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const parseWebBuildTimestamp = (buildId: string): number | null => {
  const matches = buildId.match(/^(\d{8})-(\d{4})(?:-.+)?$/);
  if (!matches) {
    return null;
  }

  const [, datePart, timePart] = matches;
  const yyyy = Number.parseInt(datePart.slice(0, 4), 10);
  const mm = Number.parseInt(datePart.slice(4, 6), 10);
  const dd = Number.parseInt(datePart.slice(6, 8), 10);
  const hh = Number.parseInt(timePart.slice(0, 2), 10);
  const min = Number.parseInt(timePart.slice(2, 4), 10);

  if ([yyyy, mm, dd, hh, min].some((value) => Number.isNaN(value))) {
    return null;
  }

  return Date.UTC(yyyy, mm - 1, dd, hh, min, 0, 0);
};

const isRemoteBuildNewer = (
  localBuild: string,
  remoteBuild: string
): boolean => {
  if (localBuild === remoteBuild) {
    return false;
  }

  const localTimestamp = parseWebBuildTimestamp(localBuild);
  const remoteTimestamp = parseWebBuildTimestamp(remoteBuild);

  if (localTimestamp !== null && remoteTimestamp !== null) {
    return remoteTimestamp > localTimestamp;
  }

  return remoteBuild.localeCompare(localBuild) > 0;
};

const readStoredWebBuild = (): string | null => {
  if (!isBrowser()) {
    return null;
  }

  return normalizeOptionalString(
    localStorage.getItem(CURRENT_WEB_BUILD_STORAGE_KEY)
  );
};

const writeStoredWebBuild = (buildId: string): void => {
  if (!isBrowser()) {
    return;
  }

  localStorage.setItem(CURRENT_WEB_BUILD_STORAGE_KEY, buildId);
};

const readReloadGuardRecord = (): ReloadGuardRecord | null => {
  if (!isBrowser()) {
    return null;
  }

  const rawValue = sessionStorage.getItem(AUTO_RELOAD_GUARD_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<ReloadGuardRecord>;
    if (
      typeof parsed.count === 'number' &&
      Number.isFinite(parsed.count) &&
      parsed.count >= 0 &&
      typeof parsed.windowStartedAt === 'number' &&
      Number.isFinite(parsed.windowStartedAt) &&
      parsed.windowStartedAt > 0
    ) {
      return {
        count: parsed.count,
        windowStartedAt: parsed.windowStartedAt,
      };
    }
  } catch {
    return null;
  }

  return null;
};

const writeReloadGuardRecord = (record: ReloadGuardRecord): void => {
  if (!isBrowser()) {
    return;
  }

  sessionStorage.setItem(AUTO_RELOAD_GUARD_STORAGE_KEY, JSON.stringify(record));
};

const consumeAutoReloadAttempt = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  try {
    const now = Date.now();
    const record = readReloadGuardRecord();
    if (!record || now - record.windowStartedAt > AUTO_RELOAD_WINDOW_MS) {
      writeReloadGuardRecord({
        count: 1,
        windowStartedAt: now,
      });

      return true;
    }

    if (record.count >= AUTO_RELOAD_MAX_ATTEMPTS) {
      return false;
    }

    writeReloadGuardRecord({
      count: record.count + 1,
      windowStartedAt: record.windowStartedAt,
    });

    return true;
  } catch {
    // Reload only after the attempt is durably recorded; otherwise blocked
    // storage could reset the guard on every page load and create a loop.
    return false;
  }
};

export const triggerGuardedClientReload = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  // One reload per document. A single chunk failure reaches this from several
  // paths (vite:preloadError, the import's own rejection, an error boundary),
  // and parallel preloads can fail together; they must not each consume a
  // guard attempt or call reload() again.
  if (autoReloadPending) {
    return true;
  }

  if (!consumeAutoReloadAttempt()) {
    return false;
  }

  autoReloadPending = true;
  window.location.reload();
  return true;
};

export const isAutoReloadPending = (): boolean => autoReloadPending;

/** Test-only: a new document starts with no reload pending. */
export const resetAutoReloadPendingForTests = (): void => {
  autoReloadPending = false;
};

const hasTrustedSessionWebBuild = (): boolean => {
  return currentSessionWebBuildSource === 'file';
};

const resolveRemoteWebBuildEndpoint = (): string | null => {
  const configuredEndpoint = normalizeOptionalString(
    import.meta.env.VITE_REMOTE_WEB_BUILD_ENDPOINT
  );

  if (configuredEndpoint) {
    return configuredEndpoint;
  }

  if (!isBrowser()) {
    return null;
  }

  // For web, same-origin gives environment-aware behavior by default.
  if (!Capacitor.isNativePlatform()) {
    return REMOTE_WEB_BUILD_PATH;
  }

  // Native apps should provide VITE_REMOTE_WEB_BUILD_ENDPOINT.
  return null;
};

const loadLocalWebBuild = async (): Promise<string | null> => {
  if (!isBrowser()) {
    return null;
  }

  if (!cachedLocalWebBuildPromise) {
    cachedLocalWebBuildPromise = fetch(LOCAL_WEB_BUILD_ENDPOINT, {
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }

        const data = (await response.json()) as { version?: unknown };
        return normalizeOptionalString(data.version);
      })
      .catch(() => null);
  }

  return cachedLocalWebBuildPromise;
};

const getNativeAppInfo = async (): Promise<{
  appVersion?: string;
  appBuildNumber?: string;
}> => {
  if (!Capacitor.isNativePlatform()) {
    return {};
  }

  if (!cachedNativeAppInfoPromise) {
    cachedNativeAppInfoPromise = App.getInfo()
      .then((info) => {
        const appVersion = normalizeOptionalString(info.version) ?? undefined;
        const appBuildNumber = normalizeOptionalString(info.build) ?? undefined;

        return {
          appVersion,
          appBuildNumber,
        };
      })
      .catch(() => ({}));
  }

  return cachedNativeAppInfoPromise;
};

const fetchRemoteWebBuild = async (): Promise<string | null> => {
  const remoteEndpoint = resolveRemoteWebBuildEndpoint();
  if (!remoteEndpoint) {
    return null;
  }

  try {
    const response = await fetch(remoteEndpoint, {
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { version?: unknown };
    return normalizeOptionalString(data.version);
  } catch {
    return null;
  }
};

export const getClientPlatform = (): ClientPlatform => {
  if (!Capacitor.isNativePlatform()) {
    return 'web';
  }

  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    return platform;
  }

  return 'web';
};

export const initializeClientBuildIdentity = async (): Promise<
  string | null
> => {
  const localBuild = await loadLocalWebBuild();
  if (localBuild) {
    currentSessionWebBuildSource = 'file';
    writeStoredWebBuild(localBuild);
    return localBuild;
  }

  const storedBuild = readStoredWebBuild();
  currentSessionWebBuildSource = storedBuild ? 'storage' : 'none';
  return storedBuild;
};

export const getCurrentWebBuild = async (): Promise<string | null> => {
  const storedBuild = readStoredWebBuild();
  if (storedBuild) {
    if (currentSessionWebBuildSource === 'none') {
      currentSessionWebBuildSource = 'storage';
    }
    return storedBuild;
  }

  return initializeClientBuildIdentity();
};

export const CLIENT_PLATFORM_HEADER = 'x-client-platform';

/**
 * The platform header, synchronously, with the web build the app has already
 * recorded. ora-be accepts a cookie-authenticated unsafe request that arrives
 * without Origin and Referer only when it carries the platform header (its
 * CSRF fallback), so every backend call that bypasses the axios instance must
 * send it; and its version gate refuses (426) a request that names a platform
 * without a build, so the build must travel with it. The stored build is
 * written at startup by `initializeClientBuildIdentity`; a caller that can
 * await should prefer `getClientMetadataHeaders`, which also loads it.
 */
export const getClientPlatformHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    [CLIENT_PLATFORM_HEADER]: getClientPlatform(),
  };

  const webBuild = readStoredWebBuild();
  if (webBuild) {
    headers['x-web-build'] = webBuild;
  }

  return headers;
};

export const getClientMetadataHeaders = async (): Promise<
  Record<string, string>
> => {
  const platform = getClientPlatform();

  const headers: Record<string, string> = {
    [CLIENT_PLATFORM_HEADER]: platform,
  };

  const webBuild = await getCurrentWebBuild();
  if (webBuild) {
    headers['x-web-build'] = webBuild;
  }

  if (platform === 'ios' || platform === 'android') {
    const nativeAppInfo = await getNativeAppInfo();

    if (nativeAppInfo.appVersion) {
      headers['x-app-version'] = nativeAppInfo.appVersion;
    }
    if (nativeAppInfo.appBuildNumber) {
      headers['x-app-build-number'] = nativeAppInfo.appBuildNumber;
    }
  }

  return headers;
};

export const checkForRemoteBuildUpdate = async (): Promise<boolean> => {
  if (!isBrowser()) {
    return false;
  }

  const localBuild = await getCurrentWebBuild();
  if (!localBuild) {
    return false;
  }

  const remoteBuild = await fetchRemoteWebBuild();
  if (!remoteBuild) {
    return false;
  }

  if (!isRemoteBuildNewer(localBuild, remoteBuild)) {
    return false;
  }

  if (!hasTrustedSessionWebBuild()) {
    return false;
  }

  return triggerGuardedClientReload();
};
